import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { Upload, Save, Loader2 } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost } from "@/components/admin/ui";

export default function SettingsPage() {
    const { settings, refresh } = useSettings();
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setForm(settings); }, [settings]);

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const r = await uploadFile(f);
            setForm({ ...form, logo_url: r.url });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Send the full settings doc; backend will overwrite
            await apiClient.put("/settings", form);
            await refresh();
            toast.success("Settings saved.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <>
            <PageHeader title="Website settings" subtitle="Business info, logo, contact details and social links." />
            <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="settings-form">
                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-5">Business</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Business name" required><input data-testid="settings-business-name" required className={inputCls} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></Field>
                        <Field label="Tagline"><input className={inputCls} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></Field>
                        <Field label="Logo" className="md:col-span-2">
                            <div className="flex gap-3 items-start">
                                {form.logo_url && <img src={resolveUrl(form.logo_url)} alt="" className="w-20 h-20 object-contain bg-sand" />}
                                <div className="flex-1 space-y-2">
                                    <input className={inputCls} value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
                                    <label className={btnGhost + " cursor-pointer"}><Upload size={14} /> Upload logo<input type="file" accept="image/*" onChange={upload} className="hidden" /></label>
                                </div>
                            </div>
                        </Field>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-5">Contact</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Phone"><input data-testid="settings-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                        <Field label="WhatsApp (digits only, e.g. 919876543210)"><input data-testid="settings-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></Field>
                        <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                        <Field label="Maps query (place name for embed)"><input className={inputCls} value={form.maps_query} onChange={(e) => setForm({ ...form, maps_query: e.target.value })} /></Field>
                        <Field label="Address" className="md:col-span-2"><textarea rows={2} className={inputCls + " resize-none"} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-5">Social media</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Instagram URL"><input className={inputCls} value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} /></Field>
                        <Field label="Facebook URL"><input className={inputCls} value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} /></Field>
                        <Field label="YouTube URL"><input className={inputCls} value={form.youtube} onChange={(e) => setForm({ ...form, youtube: e.target.value })} /></Field>
                    </div>
                </Card>

                <button disabled={saving} data-testid="settings-save" className={btnPrimary}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
                </button>
            </form>
        </>
    );
}
